import type { FunctionComponent } from 'react';

import { useContext } from 'react';

import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useAuth } from 'hooks/useAuth';

import { Box, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { breakpoints } from 'styles/theme.variables';
import Cover from 'primitives/Cover/Cover';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';

import { useMenuManagement } from 'hooks/useMenuManagement';

import { HoursErrorBanner } from 'components/MenuManagement/MenuEdit/Hours/HoursErrorBanner';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import MenuInfoModal from 'components/Modals/MenuInfoModal/MenuInfoModal';

import { useMenuNotifications } from '../../hooks/useMenuNotifications';
import { MenuTabs } from './MenuTabs';

export const MenuContent: FunctionComponent = () => {
  const { hasHoursErrors, menu, setHasHoursErrors } = useContext(MenuEdit.Context);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });
  const [opened, { open, close }] = useDisclosure(false);
  const { merchants, companies, flags: cubohUserFlags } = useAuth();
  const { getMerchantIntegrationsFromState } = useMenuManagement();

  // Only get merchant integrations if we have a menu already (not on new menu creation)
  const merchantIntegrations = menu?.merchantId
    ? getMerchantIntegrationsFromState(menu.merchantId, false)
    : [];

  const allowMapping = !!(
    (cubohUserFlags.SHOW_MENU_MAPPING || merchantIntegrations?.useV2Mapping) &&
    !merchantIntegrations?.isPosSyncInPlace
  );

  // This page has no inline banner slot design (unlike the Menu Editor list page,
  // CN-47644) — keep the floating toast the hook used to show internally.
  useMenuNotifications({
    companyId: menu?.companyId,
    onSuccess: message => {
      notifications.show({
        message,
        closeButtonProps: { 'data-testid': 'notification-close-button' },
        variant: NOTIFICATION_VARIANTS.success.label,
        icon: NOTIFICATION_VARIANTS.success.icon,
        withCloseButton: true,
        autoClose: false,
      });
    },
    onError: message => {
      notifications.show({
        message,
        closeButtonProps: { 'data-testid': 'notification-close-button' },
        variant: NOTIFICATION_VARIANTS.caution.label,
        icon: NOTIFICATION_VARIANTS.caution.icon,
        withCloseButton: true,
        autoClose: false,
      });
    },
  });

  return (
    <>
      {hasHoursErrors && (
        <Box mb="md">
          <HoursErrorBanner onClose={() => setHasHoursErrors(false)} />
        </Box>
      )}

      <Cover title={menu?.name || 'Create Menu'}>
        {!!menu && (
          <Button size={isMobile ? 'sm' : 'md'} onClick={open}>
            More info
          </Button>
        )}
      </Cover>

      <MenuTabs menu={menu} allowMapping={allowMapping} />

      <MenuInfoModal
        opened={opened}
        close={close}
        menu={menu}
        merchants={merchants}
        companies={companies}
      />
    </>
  );
};
